/* eslint-disable react/display-name */
import React, { useEffect, useState } from "react";
import { Table, Typography } from "antd";

import { useCardsContext } from "../../contexts/cardcontext";
import { getPopCountRatioListCardData, getRatioListEntityData } from "../../../utils/cardList";
import Helpers from "../../../services/helpers";
import TableGroup from "./TableGroup";
import { usePopularChartContext } from "../../contexts/popularchartcontext";
import { ENTITY_TYPES } from "utils/constants";
import { cardInstructions, sealedWaxInstructions } from "./assets";
import { useWaxesContext } from "components/contexts/WaxContext";

const getEntitiesRatioDataByTimespan = (ratios, entities, entityType) => {
  let data = null;
  let hasRatios = false;

  if (ratios && ratios.length) {
    data = ratios.reduce((acc, ratio) => {
      if (!acc[ratio.type]) acc[ratio.type] = {};
      if (!acc[ratio.type][ratio.span]) acc[ratio.type][ratio.span] = [];

      const ratioData = getRatioListEntityData(ratio, entities, entityType);
      if (ratioData) {
        acc[ratio.type][ratio.span] = [...acc[ratio.type][ratio.span], ratioData];
      }

      hasRatios = hasRatios || Boolean(ratioData);

      return acc;
    }, {});

    if (!hasRatios) {
      data = null;
    }
  }

  return data;
};

const getPopCountRatios = (cards) => {
  const data = [];

  if (cards && cards.length) {
    cards.forEach((card) => {
      if (card.population_count) {
        data.push(getPopCountRatioListCardData(card));
      }
    });
  }

  return data;
};

function EntitiesRatioList({ data, entityType = ENTITY_TYPES.CARD }) {
  const name = {
    [ENTITY_TYPES.CARD]: "Card",
    [ENTITY_TYPES.SEALED_WAX]: "Wax",
  }[entityType];

  const columns = (timespan) => [
    {
      title: `${name} 1 Name`,
      dataIndex: "entity1Name",
      key: "entity1Name",
      width: 200,
    },
    {
      title: {
        all: `${name} 1 Last Avg Sale Price`,
        last7: `${name} 1 Last 7 Day Avg Sale Price`,
        last14: `${name} 1 Last 14 Day Avg Sale Price`,
        last30: `${name} 1 Last 30 Day Avg Sale Price`,
      }[timespan],
      dataIndex: "entity1LastAvgSalePrice",
      key: "entity1LastAvgSalePrice",
      width: 100,
      align: "center",
    },
    {
      title: "Ratio",
      dataIndex: "ratio12",
      key: "ratio12",
      sorter: Helpers.sortByFieldNameNumeric("ratio12"),
      width: 90,
      align: "center",
    },
    {
      title: {
        all: `${name} 2 Last Avg Sale Price`,
        last7: `${name} 2 Last 7 Day Avg Sale Price`,
        last14: `${name} 2 Last 14 Day Avg Sale Price`,
        last30: `${name} 2 Last 30 Day Avg Sale Price`,
      }[timespan],
      dataIndex: "entity2LastAvgSalePrice",
      key: "entity2LastAvgSalePrice",
      width: 100,
      align: "center",
    },
    {
      title: `${name} 2 Name`,
      dataIndex: "entity2Name",
      key: "entity2Name",
      width: 200,
      align: "right",
    },
  ];

  return !data ? null : (
    <>
      <h2 style={{ margin: "25px 0" }}>{name} Ratios</h2>

      {data.grade?.all?.length ? <TableGroup columns={columns} ratios={data.grade} title="Grade Ratios" /> : null}
      {data.variation?.all?.length ? (
        <TableGroup columns={columns} ratios={data.variation} title="Variation Ratios" />
      ) : null}
      {data.box_type?.all?.length ? (
        <TableGroup columns={columns} ratios={data.box_type} title="Box Type Ratios" />
      ) : null}
      {(data.grade && // condition for CARD ratios
        data.grade.all &&
        !data.grade.all.length &&
        data.variation &&
        data.variation.all &&
        !data.variation.all.length) ||
      (data.box_types && // condition for SEALED_WAX ratios
        data.box_types.all &&
        !data.box_types.all.length) ? (
        <Typography.Paragraph>No ratios to show.</Typography.Paragraph>
      ) : null}
    </>
  );
}

function PopCountRatioList({ data }) {
  const columns = [
    {
      title: "Card Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (text /*, record */) => (
        <span>
          <span className="hidden-desktop inline-table-label">Card: </span> {text}
        </span>
      ),
    },
    {
      title: "Last Avg Sale Price",
      dataIndex: "avgSalePrice",
      key: "avgSalePrice",
      width: 200,
      render: (text /*, record */) => (
        <>
          <span className="hidden-desktop inline-table-label">Last Avg Sale Price: </span> {text}
        </>
      ),
    },
    {
      title: "Population Count",
      dataIndex: "popCount",
      key: "popCount",
      align: "center",
      width: 200,
      render: (text /*, record */) => (
        <>
          <span className="hidden-desktop inline-table-label">Population Count: </span> {text}
        </>
      ),
    },
    {
      title: "$ per Pop Count",
      dataIndex: "ratio",
      key: "ratio",
      align: "center",
      width: 200,
      render: (text /*, record */) => (
        <>
          <span className="hidden-desktop inline-table-label">$ per Pop Count: </span> {text}
        </>
      ),
    },
  ];

  return data.length === 0 ? null : (
    <>
      <h2 style={{ margin: "25px 0" }}>Population Count Ratios</h2>
      <Table
        className={"player-charts-ratios ant-table-mobile"}
        columns={columns}
        dataSource={data}
        pagination={{ pageSize: 50, showSizeChanger: true, showQuickJumper: true }}
      />
    </>
  );
}

export default function Ratios({ entityType = ENTITY_TYPES.CARD }) {
  const { cards, ratios: cardRatios } = useCardsContext();
  const { waxes, ratios: sealedWaxRatios } = useWaxesContext();
  const { hiddenLines } = usePopularChartContext();

  const [entityRatios, setEntityRatios] = useState();
  const [popCountRatios, setPopCountRatios] = useState([]);

  const [entities, ratios, instructions] = {
    [ENTITY_TYPES.CARD]: [cards, cardRatios, cardInstructions],
    [ENTITY_TYPES.SEALED_WAX]: [waxes, sealedWaxRatios, sealedWaxInstructions],
  }[entityType];

  const visibleEntities = entities.filter((e) => !hiddenLines[e.id]);
  const retrievedEntityRatios = getEntitiesRatioDataByTimespan(ratios, visibleEntities, entityType);

  useEffect(() => {
    setEntityRatios(retrievedEntityRatios);
    setPopCountRatios(getPopCountRatios(visibleEntities));
  }, [cards, ratios, hiddenLines]);

  if (!entityRatios && popCountRatios.length === 0) {
    return <p className="instructions">{instructions}</p>;
  }

  return (
    <>
      <EntitiesRatioList data={entityRatios} entityType={entityType} />
      <PopCountRatioList data={popCountRatios} />
    </>
  );
}
